import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fetchMe } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { CheckCircle, ArrowRight, ShieldCheck, XCircle } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team/invites/${token}`);
        setInvite(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Invite not found or already accepted");
      }
      try {
        const u = await fetchMe();
        setMe(u);
      } catch { /* not signed in — that's fine */ }
    })();
  }, [token]);

  const signInAndReturn = () => {
    const redirectUrl = `${window.location.origin}/accept-invite/${token}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/team/invites/${token}/accept`);
      toast.success("Joined the team!");
      setTimeout(() => navigate("/dashboard/team"), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept invite");
    } finally {
      setAccepting(false);
    }
  };

  const mismatch = me && invite && invite.email.toLowerCase() !== me.email.toLowerCase();

  return (
    <div className="min-h-screen bg-ink-900 text-white flex items-center justify-center px-6">
      <Toaster richColors closeButton />
      <div className="max-w-md w-full border border-white/10 bg-white/[0.02] p-8" data-testid="accept-invite-card">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-safety mb-3">SafeForm.ai · Team invite</div>

        {error && (
          <div className="mt-6">
            <XCircle size={40} weight="fill" className="text-hazard-critical mb-4" />
            <div className="font-display font-black text-2xl tracking-tight mb-3">Invite unavailable</div>
            <p className="text-white/70 text-sm leading-relaxed">{error}</p>
            <Button onClick={() => navigate("/")} className="mt-6 rounded-none bg-white text-ink-900 hover:bg-white/90" data-testid="invite-home-btn">
              Back to homepage
            </Button>
          </div>
        )}

        {invite && !error && (
          <>
            <h1 className="font-display font-black text-3xl tracking-tight leading-tight">
              You've been invited to <span className="text-safety">{invite.team_name}</span>
            </h1>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              <strong className="text-white">{invite.inviter_name || invite.inviter_email}</strong> invited{" "}
              <strong className="text-white">{invite.email}</strong> to join as a team member. Members share
              uploads, analyses and the worker roster.
            </p>

            <div className="mt-6 border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3">
              <ShieldCheck size={16} weight="bold" className="text-safety mt-0.5" />
              <div className="text-xs text-white/70 leading-relaxed">
                Face-blur and per-clip privacy controls stay identical — team members inherit
                all workspace privacy settings.
              </div>
            </div>

            {!me && (
              <Button onClick={signInAndReturn} className="mt-6 w-full rounded-none bg-safety hover:bg-safety/90 text-white" data-testid="invite-signin-btn">
                Sign in with Google to accept <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            )}

            {me && !mismatch && (
              <Button onClick={accept} disabled={accepting} className="mt-6 w-full rounded-none bg-safety hover:bg-safety/90 text-white" data-testid="invite-accept-btn">
                <CheckCircle size={16} weight="bold" className="mr-2" />
                {accepting ? "Joining…" : `Accept as ${me.email}`}
              </Button>
            )}

            {mismatch && (
              <div className="mt-6 border border-hazard-warning/40 bg-hazard-warning/5 p-4 text-sm text-white/80 leading-relaxed" data-testid="invite-mismatch">
                You're signed in as <strong>{me.email}</strong>, but this invite was sent to{" "}
                <strong>{invite.email}</strong>. Sign out and sign back in with {invite.email} to accept.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
